import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Eye, EyeOff, Mail, ExternalLink } from "lucide-react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { OTHER_SERVICES } from "../../data/otherServices";

/**
 * OtherServicesTab — iter252 Admin panel for the "خدمات أخرى" hub.
 *
 * Manages two collections:
 *   1. other_services_providers — hand-curated accredited providers per
 *      category (delivery / car_wash / workshop). CRUD + publish toggle.
 *   2. other_services_waitlist  — read-only view of buyer emails who
 *      opted in to be notified when a category launches.
 *
 * Kept intentionally simple: no filters, no pagination — the whole point
 * is this list stays small and hand-curated until the marketplace supply
 * catches up.
 */
const CATEGORIES = OTHER_SERVICES.map((s) => ({ id: s.category, ar: s.ar.title, en: s.en.title }));
const CAT_BLANK = { id: "", icon_type: "hub", ar_title: "", en_title: "", ar_desc: "", en_desc: "", published: true };
const BLANK = {
  category: CATEGORIES[0]?.id || "delivery",
  name_ar: "",
  name_en: "",
  desc_ar: "",
  desc_en: "",
  phone: "",
  external_url: "",
  tags: "",
  accredited: true,
  published: true,
};

export default function OtherServicesTab() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [providers, setProviders] = useState([]);
  const [waitlist, setWaitlist] = useState({ items: [], totals: {} });
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  // iter258 — dynamic activity types (admin-managed)
  const [dynCats, setDynCats] = useState([]);
  const [catForm, setCatForm] = useState(CAT_BLANK);
  const [catSaving, setCatSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: pr }, { data: wl }, { data: cats }] = await Promise.all([
        apiClient.get("/other-services/admin/providers"),
        apiClient.get("/other-services/admin/waitlist"),
        apiClient.get("/other-services/admin/categories"),
      ]);
      setProviders(pr.items || []);
      setWaitlist(wl || { items: [], totals: {} });
      setDynCats(cats.items || []);
    } catch (e) {
      toast.error(isAr ? "تعذّر تحميل البيانات" : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(CATEGORIES.map((c) => [c.id, []]));
    for (const p of providers) if (g[p.category]) g[p.category].push(p);
    return g;
  }, [providers]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 8),
      };
      await apiClient.post("/other-services/admin/providers", payload);
      toast.success(isAr ? "تمت الإضافة" : "Provider added");
      setForm(BLANK);
      setShowForm(false);
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  // iter258 — dynamic activity-type CRUD
  const submitCategory = async (e) => {
    e.preventDefault();
    if (catSaving) return;
    setCatSaving(true);
    try {
      await apiClient.post("/other-services/admin/categories", catForm);
      toast.success(isAr ? "تمّت إضافة النشاط" : "Activity added");
      setCatForm(CAT_BLANK);
      await load();
    } catch (err) {
      toast.error(String(err.response?.data?.detail || err.message));
    } finally {
      setCatSaving(false);
    }
  };
  const deleteCategory = async (c) => {
    const ok = window.confirm(
      isAr ? `حذف النشاط "${c.ar_title}" ؟` : `Delete activity "${c.en_title}"?`
    );
    if (!ok) return;
    try {
      await apiClient.delete(`/other-services/admin/categories/${c.id}`);
      toast.success(isAr ? "تمّ الحذف" : "Deleted");
      await load();
    } catch (err) {
      toast.error(String(err.response?.data?.detail || err.message));
    }
  };

  const togglePublish = async (p) => {
    try {
      await apiClient.patch(`/other-services/admin/providers/${p.id}`, {
        published: !p.published,
      });
      await load();
    } catch (err) {
      toast.error(isAr ? "تعذّر التحديث" : "Update failed");
    }
  };

  const remove = async (p) => {
    const ok = window.confirm(
      isAr ? `حذف "${p.name_ar}" نهائياً؟` : `Delete "${p.name_en}" permanently?`
    );
    if (!ok) return;
    try {
      await apiClient.delete(`/other-services/admin/providers/${p.id}`);
      toast.success(isAr ? "تمّ الحذف" : "Deleted");
      await load();
    } catch (err) {
      toast.error(isAr ? "تعذّر الحذف" : "Delete failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-other-services-tab">
      {/* Header + actions */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#0a0a0a]">
            {isAr ? "مزوّدو الخدمات — إدارة الأنواع والقائمة الانتظارية" : "Service providers — activity types & waitlist"}
          </h2>
          <p className="text-sm text-[#525252] mt-1 max-w-2xl">
            {isAr
              ? "أضف أنواع الخدمات المتاحة لمزوّدي الخدمات (النقل، العناية، الصيانة، إطارات، فحص…). المستخدم يختار النوع من قسم «خدمات أخرى» ثم ينتقل لمسار تسجيل النشاط التجاري المعتمد. إضافة نوع لا تنشئ شريكاً — الشريك يظهر فقط بعد تسجيل النشاط التجاري واعتماده."
              : "Curate the activity types offered to service providers (transport, care, maintenance, tyres, inspection…). Users pick a type from the 'Other services' hub and are then routed into the standard business-registration flow. Adding an activity type never creates a partner — a partner only appears after a real business registers and is verified."}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setShowWaitlist((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border border-[#0a0a0a]/15 bg-white hover:bg-[#0a0a0a] hover:text-white transition-colors"
            data-testid="toggle-waitlist"
          >
            <Mail className="w-4 h-4" />
            {isAr ? "القائمة الانتظارية" : "Waitlist"}
            <span className="rounded-full bg-[#0a0a0a] text-[#d4b56e] text-[10px] px-1.5 py-0.5">
              {(waitlist.totals?.delivery || 0) + (waitlist.totals?.car_wash || 0) + (waitlist.totals?.workshop || 0)}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold shadow"
            style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
            data-testid="new-provider-btn"
          >
            <Plus className="w-4 h-4" />
            {isAr ? "إضافة مزوّد" : "Add provider"}
          </button>
        </div>
      </div>

      {/* iter258 — dynamic activity types (admin-managed).
          Note: adding a type here NEVER creates a Partner. It only
          adds a new tile on /other-services. */}
      <div className="rounded-2xl border border-[#0a0a0a]/10 bg-[#fbf8ee]/50 p-5" data-testid="dyn-categories-panel">
        <h3 className="font-display text-lg font-semibold text-[#0a0a0a] mb-1">
          {isAr ? "أنواع الأنشطة" : "Activity types"}
        </h3>
        <p className="text-xs text-[#525252] mb-4 leading-relaxed">
          {isAr
            ? "أضف نشاطاً جديداً ليظهر مباشرة في صفحة «خدمات أخرى». إضافة نوع لا تنشئ شريكاً — الشريك يظهر فقط بعد تسجيل نشاط تجاري ومطابقته للنوع وتفعيله."
            : "Add an activity type and it appears immediately on the /other-services page. Adding a type never creates a Partner — a partner only exists after a real business registers, matches the type, and is verified."}
        </p>
        <form onSubmit={submitCategory} className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="dyn-category-form">
          <input required type="text" placeholder={isAr ? "id (a-z, 0-9, _, -)" : "id (a-z, 0-9, _, -)"}
            value={catForm.id} onChange={(e) => setCatForm({ ...catForm, id: e.target.value })}
            className="rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm" data-testid="dyn-category-id" />
          <select value={catForm.icon_type} onChange={(e) => setCatForm({ ...catForm, icon_type: e.target.value })}
            className="rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm">
            {["hub", "truck", "care", "wrench"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input required type="text" placeholder={isAr ? "العنوان (عربي)" : "Title (Arabic)"}
            value={catForm.ar_title} onChange={(e) => setCatForm({ ...catForm, ar_title: e.target.value })}
            className="rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm" />
          <input required type="text" placeholder={isAr ? "العنوان (إنجليزي)" : "Title (English)"}
            value={catForm.en_title} onChange={(e) => setCatForm({ ...catForm, en_title: e.target.value })}
            className="rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm" />
          <input required type="text" placeholder={isAr ? "الوصف (عربي)" : "Description (Arabic)"}
            value={catForm.ar_desc} onChange={(e) => setCatForm({ ...catForm, ar_desc: e.target.value })}
            className="sm:col-span-2 rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm" />
          <input required type="text" placeholder={isAr ? "الوصف (إنجليزي)" : "Description (English)"}
            value={catForm.en_desc} onChange={(e) => setCatForm({ ...catForm, en_desc: e.target.value })}
            className="sm:col-span-2 rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm" />
          <button type="submit" disabled={catSaving}
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-bold text-[#0a0a0a] disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)" }}
            data-testid="dyn-category-submit">
            {catSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isAr ? "إضافة نشاط" : "Add activity"}
          </button>
        </form>
        {dynCats.length === 0 ? (
          <p className="text-xs text-[#8a8a8a] italic">{isAr ? "لم يُضَف أي نشاط بعد." : "No custom activities yet."}</p>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {dynCats.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#0a0a0a]/10 bg-white px-3 py-2 text-xs" data-testid={`dyn-category-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#0a0a0a] truncate">{isAr ? c.ar_title : c.en_title}</div>
                  <div className="text-[10px] text-[#8a8a8a] font-mono">{c.id}</div>
                </div>
                <button onClick={() => deleteCategory(c)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title={isAr ? "حذف" : "Delete"} data-testid={`dyn-category-delete-${c.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* New-provider form */}
      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border-2 border-[#c9a55c]/40 bg-[#fbf8ee]/40 p-5 space-y-4" data-testid="new-provider-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#525252] mb-1 block">
                {isAr ? "القسم" : "Category"}
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm"
                required
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{isAr ? c.ar : c.en}</option>
                ))}
              </select>
            </div>
            <Field label={isAr ? "اسم المزوّد (عربي)" : "Provider name (Arabic)"} value={form.name_ar}
              onChange={(v) => setForm({ ...form, name_ar: v })} required />
            <Field label={isAr ? "اسم المزوّد (إنجليزي)" : "Provider name (English)"} value={form.name_en}
              onChange={(v) => setForm({ ...form, name_en: v })} required />
            <Field label={isAr ? "الهاتف" : "Phone"} value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })} placeholder="+966…" />
            <Field label={isAr ? "الرابط الخارجي" : "External URL"} value={form.external_url}
              onChange={(v) => setForm({ ...form, external_url: v })} placeholder="https://…" wide />
            <Field label={isAr ? "الوصف (عربي)" : "Description (Arabic)"} value={form.desc_ar}
              onChange={(v) => setForm({ ...form, desc_ar: v })} required wide multiline />
            <Field label={isAr ? "الوصف (إنجليزي)" : "Description (English)"} value={form.desc_en}
              onChange={(v) => setForm({ ...form, desc_en: v })} required wide multiline />
            <Field label={isAr ? "وسوم مفصولة بفواصل" : "Comma-separated tags"} value={form.tags}
              onChange={(v) => setForm({ ...form, tags: v })} placeholder="rtl, national, licensed" wide />
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-[#0a0a0a] cursor-pointer">
              <input type="checkbox" checked={form.accredited}
                onChange={(e) => setForm({ ...form, accredited: e.target.checked })} />
              {isAr ? "معتمد رسمياً" : "Accredited"}
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-[#0a0a0a] cursor-pointer">
              <input type="checkbox" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              {isAr ? "منشور" : "Published"}
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setForm(BLANK); }}
              className="rounded-full px-4 py-2 text-xs font-semibold border border-[#0a0a0a]/15 bg-white">
              {isAr ? "إلغاء" : "Cancel"}
            </button>
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-bold text-white disabled:opacity-60"
              style={{ backgroundColor: "#0a0a0a" }}
              data-testid="submit-provider">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isAr ? "حفظ" : "Save"}
            </button>
          </div>
        </form>
      )}

      {/* Waitlist */}
      {showWaitlist && (
        <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white p-5" data-testid="waitlist-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">
              {isAr ? "القائمة الانتظارية" : "Waitlist"}
            </h3>
            <div className="text-xs text-[#525252]">
              {CATEGORIES.map((c) => (
                <span key={c.id} className="inline-block mr-2 rtl:ml-2 rtl:mr-0">
                  <b>{isAr ? c.ar : c.en}:</b> {waitlist.totals?.[c.id] || 0}
                </span>
              ))}
            </div>
          </div>
          {waitlist.items.length === 0 ? (
            <p className="text-sm text-[#6b6b6b]">{isAr ? "لا يوجد مسجّلون بعد." : "Empty."}</p>
          ) : (
            <div className="overflow-x-auto -mx-5">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-[#8a8a8a]">
                  <tr>
                    <th className="text-start px-5 py-2">{isAr ? "البريد" : "Email"}</th>
                    <th className="text-start px-5 py-2">{isAr ? "القسم" : "Category"}</th>
                    <th className="text-start px-5 py-2">{isAr ? "التاريخ" : "Registered"}</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.items.map((w) => (
                    <tr key={w.id} className="border-t border-[#0a0a0a]/8">
                      <td className="px-5 py-2 font-mono text-[#0a0a0a]">{w.email}</td>
                      <td className="px-5 py-2 text-[#525252]">
                        {isAr
                          ? CATEGORIES.find((c) => c.id === w.category)?.ar || w.category
                          : CATEGORIES.find((c) => c.id === w.category)?.en || w.category}
                      </td>
                      <td className="px-5 py-2 text-[#8a8a8a]">{new Date(w.created_at).toLocaleString(isAr ? "ar-SA" : "en-US")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Providers list — grouped by category */}
      {loading ? (
        <div className="inline-flex items-center gap-2 text-[#6b6b6b]">
          <Loader2 className="w-4 h-4 animate-spin" />
          {isAr ? "جاري التحميل…" : "Loading…"}
        </div>
      ) : (
        <div className="space-y-6">
          {CATEGORIES.map((c) => (
            <div key={c.id}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-display text-base sm:text-lg font-semibold text-[#0a0a0a]">
                  {isAr ? c.ar : c.en}
                </h3>
                <span className="text-[10px] font-bold text-[#8a6f2e] bg-[#c9a55c]/15 border border-[#c9a55c]/40 rounded-full px-2 py-0.5">
                  {grouped[c.id]?.length || 0}
                </span>
              </div>
              {(!grouped[c.id] || grouped[c.id].length === 0) ? (
                <p className="text-xs text-[#8a8a8a] italic">
                  {isAr ? "لا يوجد مزوّدون بعد." : "No providers yet."}
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grouped[c.id].map((p) => (
                    <div key={p.id}
                      className="rounded-xl border border-[#0a0a0a]/10 bg-white p-4 text-xs space-y-2"
                      data-testid={`provider-row-${p.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-sm text-[#0a0a0a]">{isAr ? p.name_ar : p.name_en}</div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => togglePublish(p)}
                            className="p-1.5 rounded hover:bg-[#0a0a0a]/5"
                            title={p.published ? (isAr ? "إخفاء" : "Unpublish") : (isAr ? "نشر" : "Publish")}
                            data-testid={`toggle-publish-${p.id}`}>
                            {p.published ? <Eye className="w-4 h-4 text-[#0a7d3a]" /> : <EyeOff className="w-4 h-4 text-[#8a8a8a]" />}
                          </button>
                          <button onClick={() => remove(p)}
                            className="p-1.5 rounded hover:bg-red-50 text-red-600"
                            title={isAr ? "حذف" : "Delete"}
                            data-testid={`delete-provider-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-[#525252] leading-relaxed line-clamp-3">{isAr ? p.desc_ar : p.desc_en}</p>
                      {p.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {p.tags.map((t, i) => (
                            <span key={i} className="rounded-full px-2 py-0.5 text-[10px] bg-[#fbf8ee] text-[#6b5a2c] border border-[#c9a55c]/30">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[11px] text-[#8a8a8a] pt-1 border-t border-[#0a0a0a]/5">
                        <span dir="ltr">{p.phone || "—"}</span>
                        {p.external_url && (
                          <a href={p.external_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#8a6f2e] hover:text-[#5a4615]">
                            {isAr ? "الموقع" : "Site"}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, wide, multiline }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="text-xs font-semibold text-[#525252] mb-1 block">{label}</label>
      {multiline ? (
        <textarea
          rows={2}
          required={required}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm resize-y"
        />
      ) : (
        <input
          type="text"
          required={required}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-[#0a0a0a]/15 bg-white px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}
